import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import api from "@/api";
import { ArrowLeft, Home, LogOut, User, AlertTriangle, Bell, X, Scale } from "lucide-react";
import { subscribeToPush, isPushSubscribed, getBackendPushStatus } from "@/utils/pushNotifications";
import PatientNotificationCenter from "@/components/notifications/PatientNotificationCenter";

export default function PatientLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [badges, setBadges] = useState({});
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [hideEmergencyBanner, setHideEmergencyBanner] = useState(false);
  const pushChecked = useRef(false);

  const isRTL = language === "ar";
  const isHome = location.pathname === "/patient/home" || location.pathname === "/patient";
  // Check-in : le retour à l'accueil est porté par le bouton en haut de la page (demande propriétaire 2026-09-19)
  const hideBottomNav = location.pathname.startsWith("/patient/checkin");
  const shouldShowEmergencyBanner = ["/patient", "/patient/home"].includes(location.pathname) && !hideEmergencyBanner;

  const fetchBadges = useCallback(async () => {
    try {
      const res = await api.get("/kpi/patient/badges");
      setBadges(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  useEffect(() => {
    setHideEmergencyBanner(false);
    const handleWindowScroll = () => setHideEmergencyBanner(window.scrollY > 20);
    const handleInnerScroll = (event) => setHideEmergencyBanner((event.detail?.scrollTop || 0) > 20);
    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    window.addEventListener("hana-patient-scroll", handleInnerScroll);
    return () => {
      window.removeEventListener("scroll", handleWindowScroll);
      window.removeEventListener("hana-patient-scroll", handleInnerScroll);
    };
  }, [location.pathname]);

  // Mark section as viewed when navigating - always call, no badge check needed
  useEffect(() => {
    const sectionMap = {
      "/patient/medical-record": "documents",
      "/patient/appointments": "appointments",
    };
    const section = sectionMap[location.pathname];
    if (section) {
      api.post(`/kpi/patient/badges/mark-viewed?section=${section}`, {}, { skipAuthRedirect: true })
        .then(() => fetchBadges())
        .catch(() => { return; });
    }
  }, [location.pathname, fetchBadges]);

  // PWA push notification prompt — show for all patients (browser or standalone)
  useEffect(() => {
    if (pushChecked.current) return;
    pushChecked.current = true;

    const checkAndPromptPush = async () => {
      // Skip if not supported or user dismissed
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (localStorage.getItem("hana_push_dismissed")) return;
      const [browserSubscribed, backendStatus] = await Promise.all([isPushSubscribed(), getBackendPushStatus()]);
      if (browserSubscribed && backendStatus.subscribed) return;
      if (browserSubscribed && !backendStatus.subscribed) {
        const repaired = await subscribeToPush();
        if (repaired) return;
      }

      // Show banner for all patients after a short delay
      setShowPushBanner(true);
    };

    const timer = setTimeout(checkAndPromptPush, 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptPush = async () => {
    setShowPushBanner(false);
    await subscribeToPush();
  };

  const handleDismissPush = () => {
    setShowPushBanner(false);
    localStorage.setItem("hana_push_dismissed", "1");
  };

  return (
    <div 
      className="patient-layout min-h-screen flex flex-col pb-24"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="sticky top-0 z-30 shadow-[0_8px_24px_rgb(44,94,83,0.06)]">
        {/* Header - rassurant */}
        <header className="bg-[#FCFBF8]/90 backdrop-blur-xl border-b border-[#2C5E53]/10 px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 min-w-0 flex-shrink-0">
            <span className="font-extrabold text-[#2C5E53] text-xl tracking-normal">Hana</span>
            <span className="text-[#D97A5E] text-lg font-light">|</span>
            <span className="font-extrabold text-[#2C5E53] text-xl" style={{ fontFamily: "'Noto Sans Arabic', sans-serif" }}>هناء</span>
          </div>

          <div className="flex items-center gap-2 min-w-0">
            {/* Language toggle */}
            <div className="flex gap-0.5 p-0.5 bg-[#E9F0EE] rounded-2xl border border-[#2C5E53]/10 flex-shrink-0">
              <button
                onClick={() => changeLanguage("fr")}
                data-testid="patient-lang-fr"
                className={`text-xs px-2.5 py-1.5 rounded-xl font-bold transition-all duration-200 ${language === "fr" ? "bg-white text-[#2C5E53] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >
                FR
              </button>
              <button
                onClick={() => changeLanguage("ar")}
                data-testid="patient-lang-ar"
                className={`text-xs px-2.5 py-1.5 rounded-xl font-bold transition-all duration-200 ${language === "ar" ? "bg-white text-[#2C5E53] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >
                AR
              </button>
            </div>
            
            {/* User menu */}
            <div className="flex items-center gap-1.5 pl-2 border-l border-[#2C5E53]/10 flex-shrink-0">
              <div className="hidden min-[390px]:flex w-8 h-8 bg-[#E9F0EE] rounded-full items-center justify-center shadow-sm">
                <User className="w-4 h-4 text-[#2C5E53]" />
              </div>
              <PatientNotificationCenter initialCount={badges.notifications || 0} onRead={fetchBadges} />
              <button
                onClick={() => navigate("/legal")}
                data-testid="patient-legal-btn"
                className="p-1.5 text-slate-400 hover:text-[#2C5E53] hover:bg-[#E9F0EE] rounded-2xl transition-all duration-200"
                title={t("patient.layout.legal")}
              >
                <Scale className="w-4 h-4" />
              </button>
              <button
                onClick={logout}
                data-testid="patient-logout-btn"
                className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-2xl transition-all duration-200"
                title={t("patient.layout.logout")}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </header>
      </div>

      {/* Emergency Disclaimer Banner - uniquement sur l'accueil patient */}
      {shouldShowEmergencyBanner && (
        <div data-testid="patient-emergency-disclaimer-banner" className="bg-[#FEE2E2]/95 backdrop-blur-xl border-b border-rose-200 px-3 py-1.5 transition-[opacity,transform] duration-200">
          <div className="max-w-lg mx-auto flex items-center gap-1.5 text-[11px] sm:text-xs">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" strokeWidth={2} />
            <p className="text-rose-800 leading-snug" data-testid="patient-emergency-disclaimer-text">
              <span className="font-extrabold">{t("patient.layout.emergencyPrefix")}</span>{" "}
              <span>{t("patient.layout.emergencyAppNotice")}</span>{" "}
              <span className="hidden min-[360px]:inline">{t("patient.layout.emergencyActionBeforePhone")}</span>{" "}
              <a href="tel:141" className="font-extrabold underline underline-offset-2" data-testid="patient-emergency-phone-link">
                {t("patient.layout.emergencyPhoneLabel")}
              </a>
              <span>.</span>
            </p>
          </div>
        </div>
      )}

      {/* Push Notification Banner */}
      {showPushBanner && (
        <div data-testid="push-notification-banner" className="bg-emerald-50/90 border-b border-emerald-100 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
              <Bell className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-emerald-800">
                {t("patient.layout.pushTitle")}
              </p>
            </div>
            <button
              data-testid="push-accept-btn"
              onClick={handleAcceptPush}
              className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-2xl hover:bg-emerald-500 transition-colors flex-shrink-0"
            >
              {t("patient.layout.pushActivate")}
            </button>
            <button
              data-testid="push-dismiss-btn"
              onClick={handleDismissPush}
              className="p-2 text-emerald-400 hover:text-emerald-600 flex-shrink-0 rounded-full hover:bg-white/70 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 px-4 py-4 max-w-lg mx-auto w-full">
        {children}
      </main>

      {/* Bottom Navigation - ultra simple */}
      {!hideBottomNav && (
      <nav
        data-testid="patient-bottom-nav"
        className="fixed bottom-0 left-0 right-0 bg-[#FCFBF8]/96 backdrop-blur-xl border-t border-slate-200 flex items-center justify-center px-4 py-3 z-30 safe-area-bottom shadow-[0_-10px_26px_rgb(15,23,42,0.08)]"
      >
        <button
          type="button"
          onClick={() => isHome ? navigate("/patient/home") : navigate(-1)}
          data-testid={isHome ? "patient-bottom-home-button" : "patient-bottom-back-button"}
          className="w-full max-w-lg min-h-[52px] rounded-[1.45rem] bg-[#2C5E53] px-5 py-3 text-sm font-black text-white shadow-[0_3px_0_rgb(22,78,67,0.35),0_14px_24px_rgb(44,94,83,0.18)] transition-[transform,background-color] active:translate-y-0.5"
        >
          <span className="inline-flex items-center justify-center gap-2">
            {isHome ? <Home className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
            {isHome ? t("patient.nav.home") : (language === "ar" ? "رجوع" : "Retour")}
          </span>
        </button>
      </nav>
      )}
    </div>
  );
}
